import React, { useState, useEffect } from 'react';
import { Heart, BookOpen, Star, Trash2, Info, Users, Clock, Archive, Plus, Minus, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Game, UserGame, GameStats } from '../types';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../types';

interface GameCardProps {
  game?: Game | null;
  userGame?: UserGame;
  linkTo?: string;
  onToggleWishlist?: (gameId: string, isWishlist: boolean) => void;
  onToggleOwned?: (gameId: string, isOwned: boolean) => void;
  onTogglePlayed?: (gameId: string, isPlayed: boolean) => void;
  onRemove?: (gameId: string) => void;
  onRate?: (gameId: string, rating: number) => void;
  onUpdatePlayCount?: (gameId: string, playCount: number) => void;
  isInCollection?: boolean;
  showStats?: boolean;
  isInitiallyOwned?: boolean;
  isInitiallyWishlist?: boolean;
  isInitiallyPlayed?: boolean;
  user: UserProfile | null;
}

export function GameCard({ 
  game, 
  userGame, 
  linkTo,
  onToggleWishlist, 
  onToggleOwned,
  onTogglePlayed,
  onRemove,
  onRate,
  onUpdatePlayCount,
  isInCollection = false,
  showStats = false,
  isInitiallyOwned = false,
  isInitiallyWishlist = false,
  isInitiallyPlayed = false,
  user
}: GameCardProps) {
  // Estado interno para os botões, inicializado com as props
  const [isOwned, setIsOwned] = useState(isInitiallyOwned);
  const [isWishlist, setIsWishlist] = useState(isInitiallyWishlist);
  const [isPlayed, setIsPlayed] = useState(isInitiallyPlayed);
  // Estado para rating e play count (mantém o comportamento anterior se userGame for passado)
  const [rating, setRating] = useState(userGame?.rating || 0);
  const [playCount, setPlayCount] = useState(userGame?.play_count || 0);
  const [showPlayCounter, setShowPlayCounter] = useState(false);

  // Adicionando estado para armazenar as estatísticas atualizadas
  const [gameStats, setGameStats] = useState(game?.stats);

  // Console log para diagnóstico (ajustado)
  // console.log('[GAMECARD] Renderizando card:', { // Removido
  //   gameId: game?.id,
  //   nome: game?.name,
  //   initialOwned: isInitiallyOwned,
  //   initialWishlist: isInitiallyWishlist,
  //   initialPlayed: isInitiallyPlayed,
  //   currentOwned: isOwned,
  //   currentWishlist: isWishlist,
  //   currentPlayed: isPlayed
  // });

  // Efeito para sincronizar o estado interno se as props iniciais mudarem
  useEffect(() => {
    setIsOwned(isInitiallyOwned);
  }, [isInitiallyOwned]);

  useEffect(() => {
    setIsWishlist(isInitiallyWishlist);
  }, [isInitiallyWishlist]);

  useEffect(() => {
    setIsPlayed(isInitiallyPlayed);
    // Se marcado como não jogado, esconde o contador
    if (!isInitiallyPlayed) {
      setShowPlayCounter(false);
    }
  }, [isInitiallyPlayed]);

  // Efeito para sincronizar rating e playCount se userGame mudar (mantém compatibilidade)
  useEffect(() => {
    setRating(userGame?.rating || 0);
    setPlayCount(userGame?.play_count || 0);
  }, [userGame]);

  // Buscar estatísticas atualizadas do jogo quando o componente for montado
  useEffect(() => {
    if (game?.id && showStats) {
      const fetchGameStats = async () => {
        try {
          const { data, error } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
          if (error) {
            console.error('Erro ao buscar estatísticas do jogo RPC:', error);
            setGameStats(undefined); // Limpa em caso de erro
            return;
          }
          
          if (Array.isArray(data) && data.length > 0) {
            setGameStats(data[0] as GameStats);
          } else if (data && !Array.isArray(data)) {
            // Se data não for um array mas for um objeto, assumimos que é GameStats
            setGameStats(data as GameStats);
          } else {
            // Se data for null, undefined, ou array vazio
            setGameStats(undefined); 
          }

        } catch (error) {
          console.error('Erro ao buscar estatísticas do jogo (catch):', error);
          setGameStats(undefined); // Limpa em caso de erro no catch
        }
      };
      
      fetchGameStats();
    }
  }, [game?.id, showStats]);

  // Verificação de segurança - se game for undefined, retorna um cartão de erro
  if (!game) {
    // console.error('[GAMECARD] Erro: objeto game é null/undefined'); // Removido
    return (
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg shadow-md dark:shadow-slate-700 overflow-hidden p-4 text-center">
        <div className="text-red-500 dark:text-red-400 font-medium">Jogo não disponível</div>
      </div>
    );
  }

  // Verificação adicional para garantir que todos os dados necessários estão presentes
  if (!game.id || !game.name) {
    // console.error('[GAMECARD] Erro: game.id ou game.name ausente:', { id: game.id, name: game.name }); // Removido
    return (
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg shadow-md dark:shadow-slate-700 overflow-hidden p-4 text-center">
        <div className="text-red-500 dark:text-red-400 font-medium">Dados do jogo incompletos</div>
        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">ID: {game.id || 'ausente'}</div>
      </div>
    );
  }

  // Garantir que game.id existe com verificação de tipo
  const gameId = game?.id || '';
  // Verificação de segurança para o URL do jogo - REMOVIDO gameUrl local
  // const gameUrl = gameId ? `/games/${gameId}` : '#';

  // Define o URL padrão caso linkTo não seja fornecido
  // Prioriza slug se existir, caso contrário usa id
  const finalGameUrl = linkTo || (gameId ? (game.slug ? `/games/${game.slug}` : `/games/${gameId}`) : '#');

  // Função para renderizar as estrelas de avaliação
  const renderRatingStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          onClick={() => gameId && onRate && onRate(gameId, i)}
          className={`cursor-pointer ${i <= rating ? 'text-yellow-400 dark:text-yellow-500' : 'text-gray-300 dark:text-gray-600'}`}
          disabled={!onRate || !gameId}
          aria-label={`Avaliar com ${i} estrelas`}
        >
          <Star className="h-5 w-5" />
        </button>
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };

  // Função para renderizar o contador de jogadas
  const renderPlayCounter = () => {
    if (!onUpdatePlayCount || !showPlayCounter || !gameId || !isPlayed) return null;
    
    return (
      <div className="flex items-center justify-between bg-indigo-50 dark:bg-indigo-900/70 p-2 rounded-lg mt-2">
        <button 
          onClick={() => onUpdatePlayCount(gameId, Math.max(1, playCount - 1))}
          className="p-1 bg-white dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-800"
          disabled={playCount <= 1}
        >
          <Minus className="h-4 w-4" />
        </button>
        
        <div className="text-indigo-800 dark:text-indigo-300 font-medium">
          {playCount} {playCount === 1 ? 'jogada' : 'jogadas'}
        </div>
        
        <button 
          onClick={() => onUpdatePlayCount(gameId, playCount + 1)}
          className="p-1 bg-white dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-800"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-slate-700 overflow-hidden transition-transform hover:scale-[1.02] hover:shadow-lg">
      <div className="relative pb-[66%] overflow-hidden rounded-t-lg">
        <Link to={finalGameUrl} aria-label={`Ver detalhes de ${game?.name || 'jogo'}`}>
          <img
            src={game?.image_url || '/placeholder-game.webp'}
            alt={game?.name || 'Capa do jogo'}
            className="absolute w-full h-full object-cover transition-transform duration-300 hover:scale-110"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/placeholder-game.webp';
            }}
          />
        </Link>
      </div>
      
      <div className="p-4">
        <Link to={finalGameUrl} className="hover:underline">
          <h3 className="text-lg font-medium line-clamp-2 mb-2 text-gray-900 dark:text-gray-100" title={game?.name || 'Título desconhecido'}>
            {game?.name || 'Título desconhecido'}
          </h3>
        </Link>
        
        <div className="flex flex-wrap gap-1 mb-3">
          {game?.categories?.slice(0, 3).map((cat, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-300 rounded-full">{cat}</span>
          ))}
        </div>
        
        {/* Avaliação média */}
        {game?.average_rating && (
          <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 mb-3">
            <Star className="h-4 w-4 text-yellow-400 dark:text-yellow-500 fill-yellow-400 dark:fill-yellow-500" />
            <span><strong>{game.average_rating.toFixed(1)}</strong>/5</span>
          </div>
        )}
        
        <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 mb-2">
          {game?.min_players && game?.max_players && (
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" /> 
              {game.min_players}-{game.max_players}
            </span>
          )}
          {game?.play_time && (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> 
              {game.play_time} min
            </span>
          )}
        </div>
        
        {game?.min_age && (
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            <span className="flex items-center gap-1">
              Idade: {game.min_age}+ anos
            </span>
          </div>
        )}
      </div>
      
      <div className="p-4 border-t border-gray-100 dark:border-gray-700">
        {isInCollection && (
          <div className="mt-3 mb-3">
            {renderRatingStars()}
          </div>
        )}
        
        {/* Exibir estatísticas do jogo */}
        {showStats && (
          <div className="mt-3 mb-3 text-xs text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700 pt-2">
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {gameStats?.owned_count !== undefined && gameStats.owned_count > 0 && (
                <span title="Pessoas que possuem">
                  <span className="font-semibold">{gameStats?.owned_count}</span> possuem
                </span>
              )}
              {gameStats?.wishlist_count !== undefined && gameStats.wishlist_count > 0 && (
                <span title="Pessoas que querem">
                  <span className="font-semibold">{gameStats?.wishlist_count}</span> querem
                </span>
              )}
              {gameStats?.play_count !== undefined && gameStats.play_count > 0 && (
                <span title="Pessoas que jogaram">
                  <span className="font-semibold">{gameStats?.play_count}</span> jogaram
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Área de ações - botões lado a lado */}
        <div className="flex items-center justify-start gap-2 mt-2">
          {onToggleOwned && gameId && (
            <button 
              disabled={!user}
              onClick={() => {
                const newOwned = !isOwned;
                setIsOwned(newOwned); // Atualiza estado interno primeiro
                onToggleOwned(gameId, newOwned); // Chama o handler do pai
              }}
              className={`p-2 rounded-full ${isOwned ? 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-700 hover:dark:bg-blue-600' : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800'} ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!user ? "Faça login para adicionar à coleção" : (isOwned ? "Remover da Coleção" : "Adicionar à Coleção")}
            >
              <BookOpen className="h-5 w-5" />
            </button>
          )}
          
          {onToggleWishlist && gameId && (
            <button 
              disabled={!user}
              onClick={() => {
                const newWishlist = !isWishlist;
                setIsWishlist(newWishlist); // Atualiza estado interno primeiro
                onToggleWishlist(gameId, newWishlist); // Chama o handler do pai
              }}
              className={`p-2 rounded-full ${isWishlist ? 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-700 hover:dark:bg-red-600' : 'text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-800'} ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!user ? "Faça login para adicionar à wishlist" : (isWishlist ? "Remover da Lista de Desejos" : "Adicionar à Lista de Desejos")}
            >
              <Heart className="h-5 w-5" />
            </button>
          )}
          
          {/* Botão para marcar jogo como jogado (Toggle) */}
          {onTogglePlayed && gameId && (
            <button 
              disabled={!user}
              onClick={() => {
                const newPlayed = !isPlayed;
                setIsPlayed(newPlayed); // Atualiza estado interno primeiro
                if (!newPlayed) setShowPlayCounter(false); // Esconde contador se marcar como não jogado
                onTogglePlayed(gameId, newPlayed); // Chama o handler do pai
              }}
              className={`p-2 rounded-full ${isPlayed ? 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-700 hover:dark:bg-green-600' : 'text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-100 dark:hover:bg-green-800'} ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!user ? "Faça login para marcar como jogado" : (isPlayed ? "Desmarcar como jogado" : "Marcar como jogado")}
            >
              <PlusCircle className="h-5 w-5" />
            </button>
          )}
          
          {/* Botão para mostrar contador de jogadas */}
          {onUpdatePlayCount && isPlayed && gameId && (
            <button 
              disabled={!user}
              onClick={() => setShowPlayCounter(!showPlayCounter)}
              className={`p-2 rounded-full ${showPlayCounter ? 'text-indigo-600 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-700 hover:dark:bg-indigo-600' : 'text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-800'} ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!user ? "Faça login para gerenciar jogadas" : "Gerenciar contagem de jogadas"}
            >
              <Plus className="h-5 w-5" />
            </button>
          )}
          
          {/* Botão de Remover só aparece se onRemove existir (e usuário logado) */}
          {onRemove && gameId && user && (
            <button
              disabled={!user}
              onClick={() => onRemove(gameId)}
              className={`p-2 rounded-full text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-800 ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!user ? "Faça login para remover" : "Remover da coleção"}
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
          
          <Link 
            to={finalGameUrl} 
            className="p-2 rounded-full text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-800 ml-auto"
            title="Ver detalhes do jogo"
          >
            <Info className="h-5 w-5" />
          </Link>
        </div>
        
        {renderPlayCounter()}
      </div>
    </div>
  );
}